// Local registry of tenants created from the registration console.
// The API has no "email sent" tracking, so this keeps it in localStorage.
import type { TenantEmailRecord, TenantRegistryEntry } from '../types/tenantRegistration';

const REGISTRY_KEY = 'C2_tenantRegistry';

function read(): TenantRegistryEntry[]
{
  try
  {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  }
  catch
  {
    return [];
  }
}

function write(entries: TenantRegistryEntry[]): void
{
  try
  {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(entries));
  }
  catch (error)
  {
    console.error('❌ Could not persist tenant registry:', error);
  }
}

function keyOf(tenancyName: string): string
{
  return tenancyName.trim().toLowerCase();
}

export const tenantRegistry = {
  getAll(): TenantRegistryEntry[]
  {
    return read();
  },

  get(tenancyName: string): TenantRegistryEntry | undefined
  {
    const key = keyOf(tenancyName);
    return read().find(entry => keyOf(entry.tenancyName) === key);
  },

  upsert(entry: TenantRegistryEntry): TenantRegistryEntry
  {
    const entries = read();
    const key = keyOf(entry.tenancyName);
    const index = entries.findIndex(item => keyOf(item.tenancyName) === key);
    if (index >= 0)
    {
      entries[index] = { ...entries[index], ...entry };
    }
    else
    {
      entries.push(entry);
    }
    write(entries);
    return entry;
  },

  /** Creates a minimal entry for a tenant that only exists in the API */
  ensure(tenancyName: string, emailAddress: string, tenantUrl: string): TenantRegistryEntry
  {
    const existing = tenantRegistry.get(tenancyName);
    if (existing) return existing;
    return tenantRegistry.upsert({
      tenancyName,
      emailAddress,
      firstName: '',
      lastName: '',
      tenantUrl,
      serverUrl: '',
      createdAt: '',
      createdBy: '',
      emails: [],
    });
  },

  addEmail(tenancyName: string, record: TenantEmailRecord): void
  {
    const entry = tenantRegistry.get(tenancyName);
    if (!entry) return;
    entry.emails = [...(entry.emails || []), record];
    tenantRegistry.upsert(entry);
  },

  setServerUrl(tenancyName: string, serverUrl: string): void
  {
    const entry = tenantRegistry.get(tenancyName);
    if (!entry) return;
    entry.serverUrl = serverUrl;
    tenantRegistry.upsert(entry);
  },

  remove(tenancyName: string): void
  {
    const key = keyOf(tenancyName);
    write(read().filter(entry => keyOf(entry.tenancyName) !== key));
  },
};
